/**
 * The small pieces every card on the Overview draws a number with.
 *
 * Split out of `HomePage.tsx` by slice 20, unchanged in behaviour and shortened
 * in copy. Two rules survive intact from the file they came from, and they are
 * the reason this is a separate module rather than three components copied into
 * three cards:
 *
 * **A RATE IS NEVER A BARE PERCENTAGE.** {@link RateKpi} draws the AWS
 * threshold it is measured against, as a mark on a bar, because a number and a
 * separate sentence about a limit makes the reader do the comparison. 4% means
 * nothing; "4%, and Amazon reviews accounts above 5%" is a decision. There is
 * no way to render a rate through this module without its threshold, which is
 * what makes the rule structural rather than remembered.
 *
 * **BELOW `OVERVIEW_MIN_VOLUME_FOR_RATE` REPORTED OUTCOMES THERE IS NO
 * PERCENTAGE AT ALL.** Not greyed, not approximate — the counts, and a sentence
 * saying why. Reported outcomes — delivered plus bounced — since slice 19; it was
 * accepted messages before. The contract's builder leaves `reading.rate` as `null` under the
 * floor, **and this module asks the floor again** — the schema does not require
 * the builder to have been used, and trusting it printed 2.5% over forty
 * messages; see `rateOverTheFloor`. A bounce rate over forty messages is noise
 * wearing a percentage sign: one bad address reads as 2.5% and puts a workspace
 * visually half way to a review it is nowhere near.
 *
 * And the third, which is a different statement from either: **"nothing sent
 * yet" and "0% bounce rate" are different sentences and only one is ever true.**
 * Nothing accepted is its own arm here, and so is nothing *reported*, both above
 * the volume floor arm, because a rate over nothing is undefined rather than
 * small. The first is asked of `accepted` and never of the denominator: a window
 * whose every email Amazon rejected has no settled outcome and nothing waiting,
 * and the tiles used to call that "Nothing sent yet" (review).
 *
 * ## WHAT SLICE 20 CHANGED, AND WHAT IT DID NOT
 *
 * The tiles are the same three arms and the same contract. What went is the
 * *paragraph* under each one: the old file explained, in four to six lines per
 * tile, why a rate needs volume and what a bounce rate predicts. That was
 * reasoning for the next builder and it lives in this header now. What stays
 * visible is the four-word version, because a skimmer never opens a tooltip and
 * the honesty about a number is the half that is load-bearing.
 *
 * **That last sentence is about tooltips and must not be read as "nothing may
 * fold".** A `<details>` is not a tooltip: it is keyboard-reachable, it exists
 * on touch, and its children stay in the DOM so Ctrl+F finds them. Slice 39a
 * split each `meaning` at the line {@link TileDetail} draws — the half a reader
 * could misread the figure without stays on the screen, the half that explains
 * how it was built folds. The honesty is still the visible half; it simply
 * stopped carrying the methodology with it.
 *
 * `meaning` stayed a prop rather than becoming a constant in here. The sentence
 * differs per KPI, it is written where the KPI is composed, and a `Record` keyed
 * on a label would be a second place to look for a sentence.
 */
import { OVERVIEW_MIN_VOLUME_FOR_RATE, type HealthBand, type OverviewRateKpi, type RateReading } from '@wend/shared';
import {
  Badge,
  Kicker,
  NoFigure,
  StateChip,
  cn,
  type AbsentFigureState,
  type BadgeTone,
} from '@wend/ui';

import { MoreDetail } from '../templates/MoreDetail';

import { formatCount } from '../../lib/format';
import { formatRate } from './overview-format';

/* -------------------------------------------------------------------------- */
/* The footnote a tile may carry, and the rule for what is allowed in one      */
/* -------------------------------------------------------------------------- */

/**
 * A tile's second sentence, folded away until somebody asks for it.
 *
 * ## THE TEST, AND IT IS NOT "IS THIS SENTENCE LONG"
 *
 * `MoreDetail`'s own header states the rule this implements: *anything a person
 * could draw a false conclusion without is not allowed in here at all.* So the
 * question asked of every sentence on this page is not whether it is long. It
 * is:
 *
 *   **If a reader never opens this, can they misread the number above it?**
 *
 * Yes -> it stays on the screen, in as few words as it can be said in.
 * No  -> it comes in here.
 *
 * Which sorts the sends card's prose cleanly. *"It is not a delivery"* changes
 * what `Accepted 1,116` means and stays visible — it is the denominator
 * confusion behind one of this project's two criticals, and folding it would be
 * hiding the correction and leaving the mistake. *"Nothing on this card is
 * measured against it"* explains how the card was built; a reader who never
 * opens it still reads 1,116 correctly. That one folds.
 *
 * ## WHAT MAY NEVER BE FOLDED, WHICH IS THE HALF WORTH WRITING DOWN
 *
 * **That a figure is missing, and which absence it is.** Never the fact, and
 * never the word for it — the dash, the `StateChip`, the headline sentence of a
 * refusal arm all stay on the screen.
 *
 * **Why it is missing may fold**, and this is the correction the suite made to
 * the first draft of this header. That draft asserted refusals fold nothing at
 * all, and three probes failed against code that was already right:
 * `UnreportedDetail` has folded the *cause* of the never-reported state since
 * slice 25, with the chips announcing the absence left above it, and
 * `EngagementTiles` does the same — "Tracking off" visible, the reason behind
 * "Why". So the line runs between **that** and **why**, not between present and
 * absent:
 *
 *   that a figure is absent, and which absence  ->  on screen, always
 *   why it is absent                            ->  may fold
 *
 * It is the same test as everywhere else here. A dash whose chip reads "Tracking
 * off" cannot be misread by somebody who never opens the fold. A bare dash with
 * its reason folded away can be, and that is the one this rule forbids — which
 * is why `AbsentRateTile` takes no `detail`: its `meaning` says what the missing
 * rate would have predicted, and that is a caveat, not a cause.
 *
 * `DeliverabilityKpi`'s five refusal arms therefore keep every word of their own
 * on the screen. The one sentence folded out of that component is its footer,
 * which is true in all five states and load-bearing in none of them.
 *
 * **An empty state.** "Nothing falls in September" behind a chevron is a card
 * that looks broken.
 *
 * **A sentence that is already the short version.** `OPPORTUNITY_SUPPRESSION_NOTE`
 * was cut from fifty words to one sentence for this exact reason; folding it now
 * would spend the same decision twice and leave the page saying less.
 *
 * ## THE SUITE ENFORCES THE DIRECTION, NOT THE TASTE
 *
 * `visibleBlocks` in `verify-dashboard` strips `<details>` before it reads the
 * page, so folded text is invisible to every prose probe — which is exactly
 * right, because it is invisible to a reader too. A caveat moved in here
 * therefore *fails* the probe that asserts it is on the screen. That is the
 * mechanism: load-bearing sentences cannot be quietly folded later without the
 * suite saying so.
 */
export interface TileDetail {
  /**
   * Named, never teased. `MoreDetail` argues this at length: "More" leaves a
   * reader unable to tell whether this is where their question was answered, so
   * they open all of them once and then none of them.
   */
  readonly summary: string;
  readonly body: string;
}

/* -------------------------------------------------------------------------- */
/* One count, with what it counts                                              */
/* -------------------------------------------------------------------------- */

/**
 * One count with its label and a line saying what it counts.
 *
 * The caption is not decoration. `accepted` and `delivered` are two different
 * events and the gap between them is a KPI of its own; a column of bare numbers
 * under bare nouns is how the two get read as the same thing.
 */
export function Figure({
  label,
  value,
  caption,
  tone = 'plain',
}: {
  label: string;
  value: number;
  caption: string;
  tone?: 'plain' | 'quiet';
}) {
  return (
    <div className="flex min-w-[8.5rem] flex-1 flex-col gap-1.5">
      <Kicker as="dt">{label}</Kicker>
      <dd className="flex flex-col gap-1">
        <span
          className={cn(
            figureRole('text-[1.75rem]'),
            tone === 'quiet' ? 'text-muted' : 'text-text',
          )}
        >
          {formatCount(value)}
        </span>
        <span className="text-[0.8rem] leading-snug text-muted">{caption}</span>
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* A tile with no figure in it                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The chrome every tile on this row shares, so a tile with a figure and a tile
 * without one are the same object with the same edges.
 *
 * That is the whole point of drawing the absent ones at all: the reader sees
 * *three rates, none of them known*, at a glance and in the shape they will be
 * in once reports arrive — rather than a paragraph where a row used to be, which
 * is what the never-reported state rendered before slice 25 and why the page had
 * no figure above the fold.
 */
/*
 * SLICE 66: THE BLUEPRINT'S METRIC PANEL, WORN BY HAND RATHER THAN MOUNTED.
 *
 * The approved Blueprint suite gives every figure one skin — a mono kicker at
 * 0.13em, then a sans figure at -0.04em tracking with tabular digits, on a
 * panel — and `@wend/ui`'s `MetricPanel` is that skin as a component. These
 * tiles take the skin and not the component, for three reasons that are each
 * about this page's honesty rather than taste:
 *
 *   - `MetricPanel`'s figure is a measured count or an absent node. A tile here
 *     has five arms, three of which are sentences ("Nothing sent yet") that are
 *     neither — forcing them through the absent slot would draw a dash where
 *     the page has always said, in words, which absence it is.
 *   - The rate is drawn in its band's colour (`BAND_TEXT`), a verdict against
 *     Amazon's line. `MetricPanel` draws every figure in `text`.
 *   - The label is an `h3`, so a screen reader can walk the row by heading.
 *     (`MetricPanel` grew `kickerAs` later in the slice, so this one alone
 *     would no longer be a reason; the two above still are.)
 *
 * So the constants below are the panel's skin, and `figureRole` is its figure
 * type role, shared with `EngagementTiles` so a tile with a figure and a tile
 * without one stay the same object. Counts went from mono to sans here: the
 * suite's rule (R6) is that mono's dotted zero reads as an 8 at a glance, and
 * a misread digit is the one error a dashboard cannot afford.
 *
 * No tile is washed. The first slice 66 build washed Accepted as "the card's
 * headline count", after the Blueprint's one washed "matches now" panel. But
 * Accepted is the number this card's copy works hardest to keep from being
 * read as the outcome — "It is not a delivery", "Accepted is not delivered" —
 * and in the never-reported state delivery is unknown, so the wash promoted
 * exactly the figure the sentences demote (slice 66 review). The rates carry
 * the verdict in words and their band colour; they need no wash either.
 */
const FIGURE_ROLE = 'font-semibold leading-[1.05] tracking-[-0.04em] tabular-nums';
/**
 * The figure role at one size, size FIRST. A function and not a constant
 * spread beside a size, because `cn` is tailwind-merge and tailwind-merge drops
 * a `leading-*` class whenever a font-size class follows it in the same call:
 * `cn(FIGURE_ROLE, 'text-[2rem]')` rendered with no line-height, and every
 * tile's figure fell back to 1.5 (slice 66 review). Pass colour classes after
 * it, never another size.
 */
export function figureRole(size: `text-[${string}]`): string {
  return `${size} ${FIGURE_ROLE}`;
}
const TILE_BASE = 'flex min-w-0 flex-1 basis-[13rem] flex-col gap-3 rounded-base border p-4';
export const TILE_SURFACE = {
  plain: 'border-line bg-white/[0.04]',
} as const;
const TILE = cn(TILE_BASE, TILE_SURFACE.plain);
const TILE_MEANING = 'mt-auto border-t border-line pt-2.5 text-[0.78rem] leading-relaxed text-muted';

/**
 * A count that is genuinely a count, at the size of the rates beside it.
 *
 * `accepted` in the never-reported state is the one true figure this page has
 * there, and it was previously a 0.9rem mono line inside a paragraph. It is the
 * reader's only way to recognise their own sending in that state.
 */
export function CountTile({
  label,
  value,
  meaning,
  detail,
}: {
  label: string;
  value: number;
  /** The half a reader could misread the figure without. Always on screen. */
  meaning: string;
  /** The half they could not. See {@link TileDetail}. */
  detail?: TileDetail;
}) {
  return (
    <div className={TILE}>
      <Kicker as="h3">{label}</Kicker>
      <span className={cn(figureRole('text-[2rem]'), 'text-text')}>{formatCount(value)}</span>
      {/* The wrapper carries `TILE_MEANING` — and so its `mt-auto` — so the
          footnote is pushed to the tile's floor *with* the sentence it
          qualifies, rather than drifting away from it. */}
      <div className={TILE_MEANING}>
        <p>{meaning}</p>
        {detail !== undefined && (
          <MoreDetail summary={detail.summary} className="mt-1.5">
            <p>{detail.body}</p>
          </MoreDetail>
        )}
      </div>
    </div>
  );
}

/**
 * A rate tile with no rate in it — an em-dash and the state, never a zero.
 *
 * **There is no `value` prop, and that is the rule made structural.** This is
 * the tile that stands where `0.00% — Under the line` was once printed in green
 * over a thousand emails nobody had reported on. A component that could accept a
 * number here is a component that can be handed the zero again.
 *
 * `meaning` stays, because what a bounce rate *predicts* is true whether or not
 * anybody has measured it — and it is the sentence that tells a reader why the
 * missing figure is worth caring about.
 */
export function AbsentRateTile({
  label,
  state,
  chipLabel,
  meaning,
}: {
  label: string;
  state: AbsentFigureState;
  /** Overrides the state's word where the screen calls it something plainer. */
  chipLabel?: string;
  meaning: string;
}) {
  return (
    <div className={TILE} data-role="absent-rate">
      <Kicker as="h3">{label}</Kicker>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
        <NoFigure state={state} className="text-[2rem] leading-none" />
        <StateChip state={state} {...(chipLabel === undefined ? {} : { label: chipLabel })} />
      </div>
      <p className={TILE_MEANING}>{meaning}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The floor, asked here as well as on the wire                                */
/* -------------------------------------------------------------------------- */

/**
 * The rate to print, or `null` — and `null` whenever the denominator is under
 * {@link OVERVIEW_MIN_VOLUME_FOR_RATE}, **whatever the wire says**.
 *
 * The header used to say the contract "does the enforcing" and that there is
 * "nothing here to accidentally format". The first half is a builder's promise:
 * `rateReading()` nulls the rate under the floor, but `rateReadingSchema` does
 * not require it, so `{ rate: 0.025, denominator: 40 }` parses — and this tile
 * printed **2.5% "Under the line"** in green over forty messages, which is the
 * one thing rule 3 of the Overview exists to prevent. Found by the prove pass
 * handing the component the shape rather than the builder's output.
 *
 * Tightening the schema is the other half and was deliberately not done in the
 * same change: `rateReadingSchema` is shared by the engagement, ISP,
 * list-safety and WhatsApp results contracts, each with its own floor and its
 * own API suite, so a refinement there is a contract change to four responses.
 * The component asking the one number it is drawn against is needed either way
 * — it is cheap, and it cannot drift from the constant it imports.
 */
function rateOverTheFloor(reading: RateReading): number | null {
  if (reading.rate === null) return null;
  if (reading.denominator < OVERVIEW_MIN_VOLUME_FOR_RATE) return null;
  return reading.rate;
}

/* -------------------------------------------------------------------------- */
/* The three renderings of a verdict                                           */
/* -------------------------------------------------------------------------- */

export const BAND_BAR: Readonly<Record<HealthBand, string>> = {
  ok: 'bg-good',
  warning: 'bg-warn',
  critical: 'bg-bad',
};

export const BAND_TEXT: Readonly<Record<HealthBand, string>> = {
  ok: 'text-good',
  warning: 'text-warn',
  critical: 'text-bad',
};

interface BandVerdict {
  readonly label: string;
  readonly tone: BadgeTone;
}

export const BAND_VERDICT: Readonly<Record<HealthBand, BandVerdict>> = {
  ok: { label: 'Under the line', tone: 'good' },
  warning: { label: 'Climbing towards the line', tone: 'warn' },
  critical: { label: 'Over the line', tone: 'bad' },
};

/**
 * The rate, drawn against the threshold rather than beside it.
 *
 * The scale is set so the threshold always sits at or left of the midpoint —
 * `threshold * 2` — which means a healthy workspace shows a short bar with a
 * long stretch of room after the mark, and a workspace in trouble runs past it.
 * A scale that ended at the threshold would peg every account in trouble at
 * "full" and hide the difference between 6% and 30%.
 *
 * It is `aria-hidden` on purpose: the rate, the threshold and the verdict are
 * all already text in the tile above it, so a screen reader gets the comparison
 * in words rather than a second, wordless copy of it.
 *
 * **This is a status colour inside a bar and that is allowed — it is not a
 * chart.** Slice 20's rule is that `--color-good` is never drawn inside a
 * *plotting area*, where it would be one mark among many and confusable with
 * `--color-series-b`. Here the whole bar is the verdict, it has one colour, and
 * the words beside it say the same thing.
 */
export function ThresholdBar({
  rate,
  threshold,
  band,
}: {
  rate: number;
  threshold: number;
  band: HealthBand;
}) {
  const scaleMax = Math.max(threshold * 2, rate * 1.2);
  const fill = scaleMax === 0 ? 0 : Math.min(1, rate / scaleMax);
  const mark = scaleMax === 0 ? 0 : Math.min(1, threshold / scaleMax);

  return (
    /* Forced colours replace every background with the system's Canvas colour,
       and this bar was drawn in nothing else, so the rate and Amazon's mark both
       vanished (review). The track gets an outline, the fill the system's
       highlight, and the mark a border — each of which survives. */
    <div
      aria-hidden="true"
      className="relative h-2 w-full rounded-full bg-white/[0.06] forced-colors:border forced-colors:border-[CanvasText]"
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] forced-colors:bg-[Highlight]',
          BAND_BAR[band],
        )}
        style={{ width: `${(fill * 100).toFixed(2)}%` }}
      />
      <span
        className="absolute -top-1 bottom-[-0.25rem] w-px bg-text/60 forced-colors:w-0 forced-colors:border-l-2 forced-colors:border-[CanvasText]"
        style={{ left: `${(mark * 100).toFixed(2)}%` }}
      />
    </div>
  );
}

/**
 * The unsettled remainder, said beside a rate rather than dissolved into it.
 *
 * Since slice 19 every rate on these tiles is over *settled* outcomes — the
 * accepted emails Amazon has reported a delivery or a bounce for — so the rest of
 * what was accepted is not in the figure, and a reader who sent 1,116 and sees
 * "of 12" deserves to be told where the other 1,104 went. Nothing when there is
 * nothing waiting.
 */
function AwaitingRemainder({ awaiting }: { awaiting: number }) {
  if (awaiting === 0) return null;
  return (
    <p data-role="awaiting-remainder" className="text-[0.78rem] leading-relaxed text-muted">
      {formatCount(awaiting)} more accepted are still waiting for a delivery or bounce report, and
      are not in this figure.
    </p>
  );
}

/**
 * A headline rate: bounce and complaint, the two that can cost a tenant their
 * AWS account.
 *
 * Five outcomes, and they are five different sentences rather than five
 * renderings of one:
 *
 *   - **Nothing accepted at all.** "Nothing sent yet." A rate over an empty
 *     denominator is not zero, it is undefined, and a confident 0% here would
 *     be the screen's first lie.
 *   - **Accepted, and no delivery or bounce reported for any of it.** Not
 *     "nothing sent" — mail went — and not a rate: the denominator is settled
 *     outcomes (slice 19), and there are none. The tiles never reach this arm
 *     while *every* accepted message waits — `noOutcomeReported` replaces them
 *     first — so what reaches it has failures in it: every email rejected, or
 *     some rejected with the rest still waiting.
 *   - **Bounces reported and not one delivery, with messages waiting.** The API
 *     withholds the rate (`onlyBouncesReported`); this says why, with the counts.
 *   - **Some reported, but under the floor.** The raw counts, and a short line
 *     saying a rate needs more reported outcomes before it means anything.
 *   - **Enough reported.** The percentage, the verdict, and the threshold
 *     marked.
 *
 * The last two carry the unsettled remainder beside them — see
 * {@link AwaitingRemainder}.
 */
export function RateKpi({
  label,
  kpi,
  accepted,
  awaiting,
  onlyBounces,
  bouncedOutsideCohort,
  numeratorNoun,
  thresholdSentence,
  nothingYetSentence,
  meaning,
}: {
  label: string;
  kpi: OverviewRateKpi;
  /** Email accepted in the window. What "nothing sent" is asked of — never the denominator. */
  accepted: number;
  /** Accepted, with no delivery or bounce reported — not in the denominator. */
  awaiting: number;
  /** `onlyBouncesReported` of the window: the rate is withheld, and this says why. */
  onlyBounces: boolean;
  /**
   * Bounces Amazon reported that no rate here divides by, because they reached
   * Wend through the sending identity rather than the delivery reports set.
   * Threaded in so {@link NothingSettled} cannot deny them — see
   * `cohortReading` in `SendingHealthCard`.
   */
  bouncedOutsideCohort: number;
  numeratorNoun: string;
  thresholdSentence: string;
  nothingYetSentence: string;
  meaning: string;
}) {
  const { reading, threshold, band } = kpi;
  const rate = rateOverTheFloor(reading);

  return (
    <div className={TILE}>
      <Kicker as="h3">{label}</Kicker>

      {accepted === 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-[1rem] font-semibold tracking-[-0.015em] text-text">Nothing sent yet</p>
          <p className="text-[0.82rem] leading-relaxed text-muted">{nothingYetSentence}</p>
        </div>
      ) : reading.denominator === 0 ? (
        <NothingSettled accepted={accepted} awaiting={awaiting} bouncedOutsideCohort={bouncedOutsideCohort} />
      ) : onlyBounces ? (
        <div className="flex flex-col gap-1.5">
          {/* The tile's own count stays: on the complaint tile it is the one
              figure the shared sentence does not carry (review). */}
          <p className={cn(figureRole('text-[1.5rem]'), 'text-text')}>
            {formatCount(reading.numerator)}
            <span className="text-[0.9rem] font-normal tracking-normal text-muted"> {numeratorNoun}</span>
          </p>
          <OnlyBouncesReported bounced={reading.denominator} awaiting={awaiting} />
        </div>
      ) : rate === null || band === null ? (
        <div className="flex flex-col gap-1.5">
          <p className={cn(figureRole('text-[1.5rem]'), 'text-text')}>
            {formatCount(reading.numerator)}
            <span className="text-[0.9rem] font-normal tracking-normal text-muted">
              {' '}
              {numeratorNoun} of {formatCount(reading.denominator)} with a delivery or bounce
              reported
            </span>
          </p>
          <p className="text-[0.82rem] leading-relaxed text-muted">
            Counts, not a percentage — a rate needs {formatCount(OVERVIEW_MIN_VOLUME_FOR_RATE)}{' '}
            emails with a delivery or bounce reported before it means anything.
          </p>
          <AwaitingRemainder awaiting={awaiting} />
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className={cn(figureRole('text-[2rem]'), BAND_TEXT[band])}>
              {formatRate(rate)}
            </span>
            <Badge tone={BAND_VERDICT[band].tone}>{BAND_VERDICT[band].label}</Badge>
          </div>

          <ThresholdBar rate={rate} threshold={threshold} band={band} />

          <p className="text-[0.82rem] leading-relaxed text-muted">
            {formatCount(reading.numerator)} {numeratorNoun} of{' '}
            {formatCount(reading.denominator)} with a delivery or bounce reported.{' '}
            {thresholdSentence}
          </p>
          <AwaitingRemainder awaiting={awaiting} />
        </div>
      )}

      <p className="mt-auto border-t border-line pt-2.5 text-[0.78rem] leading-relaxed text-muted">
        {meaning}
      </p>
    </div>
  );
}

/**
 * Accepted, and not one delivery or bounce reported: the rate has no denominator,
 * and saying "nothing sent" would deny mail that went. Shared by the three tiles,
 * which reach it on the same condition.
 *
 * The count is `accepted`, not `awaiting`. What reaches this arm with messages
 * waiting is replaced by `noOutcomeReported` first, so what is left is mostly a
 * window of failures — and that window has nothing waiting, which printed "0
 * accepted" over mail Amazon had rejected (review). "Yet" only while something
 * still is.
 */
function NothingSettled({
  accepted,
  awaiting,
  bouncedOutsideCohort,
}: {
  accepted: number;
  awaiting: number;
  /** See {@link RateKpi}. Bounces Amazon reported that no rate here divides by. */
  bouncedOutsideCohort: number;
}) {
  /*
   * SLICE 25: THIS ARM USED TO DENY REPORTS THE SCREEN WAS HOLDING.
   *
   * It said, unconditionally, that Amazon had reported no delivery and no bounce
   * for any accepted message — because "nothing settled" means the *cohort* is
   * empty, and slice 24 narrowed that cohort to mail that went through Wend's
   * delivery reports set. A workspace whose bounces arrive through the sending
   * identity's own topic instead has an empty cohort and a full inbox of
   * bounces, and was told none had been reported.
   *
   * The rate stays withheld either way — there is still no denominator, and
   * inventing one is what slice 24 item 16 fixed. What changes is that the
   * reports Wend does hold are named instead of denied.
   */
  const reportedElsewhere = bouncedOutsideCohort > 0;

  return (
    <div data-state="nothing-settled" data-outside-cohort={reportedElsewhere} className="flex flex-col gap-1.5">
      <p className="text-[1rem] font-semibold tracking-[-0.015em] text-text">
        {reportedElsewhere
          ? 'Reported, but not through the delivery reports set'
          : awaiting > 0
            ? 'No delivery or bounce reported yet'
            : 'No delivery or bounce'}
      </p>
      <p className="text-[0.82rem] leading-relaxed text-muted">
        {reportedElsewhere ? (
          <>
            {formatCount(accepted)} accepted. Amazon has reported {formatCount(bouncedOutsideCohort)}{' '}
            bounced, but not through the delivery reports set these rates are measured over — and
            that is the only source that reports a delivery, so there is still nothing for a rate to
            be a fraction of.
          </>
        ) : (
          <>
            {formatCount(accepted)} accepted, and Amazon has reported no delivery and no bounce for
            any of them — so there is nothing for a rate to be a fraction of.
            {awaiting === 0 && ' The card below counts why they did not arrive.'}
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Bounces reported, not one delivery, and messages still waiting — the rate the
 * API withheld, and the reason in words. See `onlyBouncesReported`: over the
 * bounces alone, every rate on these tiles would read as though everything
 * reported on had bounced. The counts stay, because each is a fact.
 */
function OnlyBouncesReported({ bounced, awaiting }: { bounced: number; awaiting: number }) {
  return (
    <div data-state="only-bounces-reported" className="flex flex-col gap-1.5">
      <p className="text-[1rem] font-semibold tracking-[-0.015em] text-text">
        Bounces reported, but not one delivery
      </p>
      <p className="text-[0.82rem] leading-relaxed text-muted">
        Amazon has reported {formatCount(bounced)} bounced and no delivery at all, while{' '}
        {formatCount(awaiting)} more accepted have no report. A rate over the bounces alone would
        read as if every reported email bounced, so there is none — Amazon is probably not
        reporting deliveries to Wend.
      </p>
    </div>
  );
}

/**
 * Deliverability — delivered over settled outcomes, and deliberately without a
 * band.
 *
 * Over settled since slice 19, like the two rates beside it: over `accepted`, a
 * window twelve reports into a thousand-message send read as a 1% delivery
 * rate — a collapse — about mail nobody had reported on yet. The unsettled
 * remainder is said beside it instead.
 *
 * AWS publishes no deliverability threshold, so there is no line to draw.
 * Giving it one anyway would put an invented number on the screen with the same
 * visual weight as the two real ones next to it, and the reader has no way to
 * tell which of the three Amazon actually enforces. The floor is the same
 * {@link OVERVIEW_MIN_VOLUME_FOR_RATE} the two beside it use, because a ratio
 * over forty messages is no better behaved for having no threshold.
 */
export function DeliverabilityKpi({
  reading,
  accepted,
  awaiting,
  onlyBounces,
  bouncedOutsideCohort,
}: {
  reading: RateReading;
  /** Email accepted in the window. What "nothing sent" is asked of — never the denominator. */
  accepted: number;
  /** Accepted, with no delivery or bounce reported — not in the denominator. */
  awaiting: number;
  /** `onlyBouncesReported` of the window: the figure is withheld, and this says why. */
  onlyBounces: boolean;
  /** See {@link RateKpi}. */
  bouncedOutsideCohort: number;
}) {
  const rate = rateOverTheFloor(reading);
  return (
    <div className={TILE}>
      <Kicker as="h3">Deliverability</Kicker>

      {accepted === 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-[1rem] font-semibold tracking-[-0.015em] text-text">Nothing sent yet</p>
          <p className="text-[0.82rem] leading-relaxed text-muted">
            Nothing was accepted in this window, so there is nothing to have been delivered.
          </p>
        </div>
      ) : reading.denominator === 0 ? (
        <NothingSettled accepted={accepted} awaiting={awaiting} bouncedOutsideCohort={bouncedOutsideCohort} />
      ) : onlyBounces ? (
        <OnlyBouncesReported bounced={reading.denominator} awaiting={awaiting} />
      ) : rate === null ? (
        <div className="flex flex-col gap-1.5">
          <p className={cn(figureRole('text-[1.5rem]'), 'text-text')}>
            {formatCount(reading.numerator)}
            <span className="text-[0.9rem] font-normal tracking-normal text-muted">
              {' '}
              delivered of {formatCount(reading.denominator)} with a delivery or bounce reported
            </span>
          </p>
          <p className="text-[0.82rem] leading-relaxed text-muted">
            Counts, not a percentage — under {formatCount(OVERVIEW_MIN_VOLUME_FOR_RATE)} emails with
            a delivery or bounce reported the figure swings too far on too little.
          </p>
          <AwaitingRemainder awaiting={awaiting} />
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <span className={cn(figureRole('text-[2rem]'), 'text-text')}>
            {formatRate(rate)}
          </span>
          <p className="text-[0.82rem] leading-relaxed text-muted">
            {formatCount(reading.numerator)} delivered of {formatCount(reading.denominator)} with a
            delivery or bounce reported. Amazon publishes no threshold for this one, so no line is
            drawn against it.
          </p>
          <AwaitingRemainder awaiting={awaiting} />
        </div>
      )}

      {/*
        THE FOOTER FOLDS, AND IT IS THE ONE SENTENCE ON THIS CARD THAT MAY.

        It defined `accepted` against `delivered` — which the Accepted tile,
        above this one on the same card, already says on screen, in the place
        where the word `Accepted` is the headline and therefore the place a
        reader is actually misreading it. Said in both, it was one explanation
        carried twice by one card.

        Everything else here stays visible, including in all five refusal arms
        above: "Amazon publishes no threshold for this one" is the sentence this
        card cannot be read correctly without, because every other rate on the
        page draws a line and a reader who does not know this one has none will
        supply one.
      */}
      <div className="mt-auto border-t border-line pt-2.5">
        <MoreDetail summary="Accepted and delivered">
          <p>
            Accepted means Amazon took the message. Delivered means a receiving server admitted
            it.
          </p>
        </MoreDetail>
      </div>
    </div>
  );
}
